import { useState, useEffect } from "react";
import { Box, Button, Text, VStack } from "@chakra-ui/react";
import { Alert } from "@chakra-ui/react/alert";
import { MdWarning as WarningIcon } from "react-icons/md";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableIngredientItem from "@/components/SortableIngredientItem";
import { CollectionIngredient, RecipeCollection } from "@/types/api";

interface ShoppingListTabProps {
  group: RecipeCollection;
  onToggle: (ingredient: CollectionIngredient, hasIt: boolean) => void;
  onReorder: (ingredientUpdates: { id: string; index: number }[]) => void;
  onGenerate: () => void;
  isGenerating: boolean;
}

export default function ShoppingListTab({
  group,
  onToggle,
  onReorder,
  onGenerate,
  isGenerating,
}: ShoppingListTabProps) {
  const [ingredientOrder, setIngredientOrder] = useState<string[]>([]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize ingredient order when group data loads or when ingredients change
  useEffect(() => {
    if (group?.ingredients) {
      // Check if the current ingredient order matches the actual ingredients
      const currentIngredientTexts = group.ingredients.map(ing => ing.text).sort();
      const orderedTexts = ingredientOrder.slice().sort();

      const hasChanged = ingredientOrder.length === 0 ||
                        currentIngredientTexts.length !== orderedTexts.length ||
                        !currentIngredientTexts.every((text, index) => text === orderedTexts[index]);

      if (hasChanged) {
        const uncheckedIngredients = group.ingredients
          .filter((ingredient) => ingredient.status === 'dont-have')
          .map((ingredient) => ingredient.text);
        const checkedIngredients = group.ingredients
          .filter((ingredient) => ingredient.status === 'have')
          .map((ingredient) => ingredient.text);
        setIngredientOrder([...uncheckedIngredients, ...checkedIngredients]);
      }
    }
  // ingredientOrder intentionally excluded: we only recompute initial order when ingredients list changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.ingredients]);

  // Handle drag and drop reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    // Get the ingredient being dragged and the drop target
    const activeIngredient = group?.ingredients?.find(ing => ing.id === active.id);
    const overIngredient = group?.ingredients?.find(ing => ing.id === over.id);

    // Prevent dragging 'dont-have' ingredients below 'have' ingredients
    if (activeIngredient?.status === 'dont-have' && overIngredient?.status === 'have') {
      return; // Don't allow this reorder
    }

    setIngredientOrder((items) => {
      const oldIndex = items.findIndex(item =>
        group?.ingredients?.find(ing => ing.id === active.id)?.text === item
      );
      const newIndex = items.findIndex(item =>
        group?.ingredients?.find(ing => ing.id === over.id)?.text === item
      );

      // Additional check: ensure we're not moving a 'dont-have' item below a 'have' item
      if (activeIngredient?.status === 'dont-have' && overIngredient?.status === 'have') {
        // Find the last 'dont-have' item position and ensure we don't go beyond it
        const lastDontHaveIndex = items.findIndex(itemText => {
          const ingredient = group?.ingredients?.find(ing => ing.text === itemText);
          return ingredient?.status === 'have';
        }) - 1;

        if (newIndex > lastDontHaveIndex && lastDontHaveIndex >= 0) {
          return items; // Don't allow the move
        }
      }

      const newOrder = arrayMove(items, oldIndex, newIndex);

      // Send reorder to backend
      if (group?.ingredients) {
        const ingredientUpdates = newOrder.map((itemText, index) => {
          const ingredient = group.ingredients?.find(ing => ing.text === itemText);
          return {
            id: ingredient?.id || '',
            index: index
          };
        }).filter(update => update.id); // Filter out any empty IDs

        onReorder(ingredientUpdates);
      }

      return newOrder;
    });
  };

  // Get ordered ingredients based on current order
  const getOrderedIngredients = (): CollectionIngredient[] => {
    if (!group?.ingredients || ingredientOrder.length === 0) {
      return group?.ingredients || [];
    }

    const ingredientMap = new Map<string, CollectionIngredient>();
    group.ingredients.forEach((ingredient) => {
      ingredientMap.set(ingredient.text, ingredient);
    });

    return ingredientOrder
      .map(name => ingredientMap.get(name))
      .filter((ingredient): ingredient is CollectionIngredient => ingredient !== undefined);
  };

  const handleIngredientToggle = (ingredient: CollectionIngredient, hasIt: boolean) => {
    // Call parent's toggle handler
    onToggle(ingredient, hasIt);

    // Optimistically adjust local ordering so 'have' items drift to end
    setIngredientOrder(prevOrder => {
      if (!prevOrder.length) return prevOrder;
      const next = [...prevOrder];
      const idx = next.indexOf(ingredient.text);
      if (idx === -1) return next;
      next.splice(idx, 1);
      if (hasIt) {
        next.push(ingredient.text);
      } else {
        // Insert before first 'have' item (keep dont-have clustered at top)
        let insertionIndex = next.length; // default end
        for (let i = 0; i < next.length; i++) {
          const ing = group?.ingredients?.find(g => g.text === next[i]);
          if (ing && ing.status === 'have') { insertionIndex = i; break; }
        }
        next.splice(insertionIndex, 0, ingredient.text);
      }
      return next;
    });
  };

  const orderedIngredients = getOrderedIngredients();

  return (
    <>
      {/* Show alert if ingredients need regeneration */}
      {group.ingredientsNeedRegeneration && (
        <Alert.Root status="warning" mb={4}>
          <Alert.Indicator>
            <WarningIcon />
          </Alert.Indicator>
          <Alert.Content>
            <Alert.Title>Shopping list is out of date</Alert.Title>
            <Alert.Description>
              You've added or removed recipes. Click the button below to regenerate your shopping list.
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      )}

      {/* Show regenerate button if needed */}
      {group.ingredientsNeedRegeneration && (
        <Button
          bg="accent.emphasis"
          color="white"
          _hover={{ bg: "accent.emphasisHover" }}
          size="md"
          mb={4}
          onClick={onGenerate}
          loading={isGenerating}
        >
          {orderedIngredients.length > 0 ? 'Regenerate Shopping List' : 'Generate Shopping List'}
        </Button>
      )}

      {orderedIngredients.length > 0 ? (
        <VStack gap={4} align="stretch">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedIngredients.map(ing => ing.id)}
              strategy={verticalListSortingStrategy}
            >
              <VStack align="stretch" gap={1}>
                {orderedIngredients.map((ingredient) => (
                  <SortableIngredientItem
                    key={ingredient.id}
                    ingredient={ingredient}
                    onToggle={handleIngredientToggle}
                  />
                ))}
              </VStack>
            </SortableContext>
          </DndContext>
        </VStack>
      ) : (
        <Box textAlign="center" py={12}>
          {group.recipeIds && group.recipeIds.length > 0 ? (
            <>
              <Text fontFamily="heading" fontWeight="800" color="text.primary" mb={2}>No ingredients yet</Text>
              <Text fontFamily="body" fontWeight="300" color="text.muted" mb={4}>
                Click the button above to generate your shopping list
              </Text>
            </>
          ) : (
            <>
              <Text fontFamily="heading" fontWeight="800" color="text.primary" mb={2}>No ingredients yet</Text>
              <Text fontFamily="body" fontWeight="300" color="text.muted">
                Add some recipes to generate your shopping list
              </Text>
            </>
          )}
        </Box>
      )}
    </>
  );
}
